import React from 'react';
import { 
  X, 
  Calculator, 
  ClipboardList, 
  Receipt as ReceiptIcon, 
  Calendar, 
  UserPlus, 
  Boxes,
  TrendingUp,
  Settings,
  Plus,
  Compass
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ActiveView } from '../hooks/useErpPlaygroundState';

interface SpecialistQuickActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: ActiveView) => void;
  plan?: string;
  isMasterUser?: boolean;
}

export default function SpecialistQuickActionSheet({
  isOpen,
  onClose,
  onNavigate,
  plan = 'Grátis',
  isMasterUser = false
}: SpecialistQuickActionSheetProps) {
  const isBasicOrPro = !isMasterUser && (plan === 'Grátis' || plan === 'Pro');

  const actions = isBasicOrPro ? [
    {
      id: 'orcamento',
      title: 'Novo Orçamento',
      subtitle: 'Calcular preço de venda com mark-up protegido',
      icon: Calculator,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'custos',
      title: 'Cadastrar Materiais & Insumos',
      subtitle: 'Adicionar custos base e configurar Valor Hora',
      icon: Boxes,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'cockpit',
      title: 'Consultar Meu Painel',
      subtitle: 'Visualizar propostas aprovadas e metas do mês',
      icon: TrendingUp,
      iconBg: 'bg-emerald-50 border-emerald-200/80',
      iconColor: 'text-emerald-700'
    },
    {
      id: 'cadastro',
      title: 'Dados da Minha Empresa',
      subtitle: 'Atualizar logotipo, telefone e chave PIX',
      icon: Settings,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'tour',
      title: 'Como Funciona o R-Max',
      subtitle: 'Rever o tour explicativo e primeiros passos',
      icon: Compass,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    }
  ] : [
    {
      id: 'orcamento',
      title: 'Novo Orçamento',
      subtitle: 'Calcular serviços e materiais',
      icon: Calculator,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'os',
      title: 'Nova Ordem de Serviço',
      subtitle: 'Registrar execução do serviço e fotos',
      icon: ClipboardList,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'recibo',
      title: 'Emitir Recibo PIX',
      subtitle: 'Comprovante de pagamento e chave PIX',
      icon: ReceiptIcon,
      iconBg: 'bg-emerald-50 border-emerald-200/80',
      iconColor: 'text-emerald-700'
    },
    {
      id: 'agenda',
      title: 'Novo Agendamento',
      subtitle: 'Marcar horário ou visita com cliente',
      icon: Calendar,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'clientes',
      title: 'Novo Cliente',
      subtitle: 'Cadastrar nome, telefone e endereço',
      icon: UserPlus,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    },
    {
      id: 'tour',
      title: 'Como Funciona o R-Max',
      subtitle: 'Rever o tour explicativo e primeiros passos',
      icon: Compass,
      iconBg: 'bg-zinc-100 border-zinc-200/80',
      iconColor: 'text-zinc-800'
    }
  ];

  const handleAction = (viewId: string) => {
    onClose();
    onNavigate(viewId as ActiveView);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-end justify-center pointer-events-auto font-sans">
          {/* Backdrop Escurecido com Suporte a Toque para Fechar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-neutral-950/70 backdrop-blur-xs"
          />

          {/* Folha Deslizante M3 (Bottom Sheet) */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 26, stiffness: 300 }}
            className="relative w-full max-w-xl bg-white rounded-t-3xl shadow-2xl border-t border-zinc-200/90 z-10 p-5 pb-8 space-y-4 max-h-[85vh] overflow-y-auto"
          >
            {/* Puxador M3 (Drag Handle) */}
            <div className="w-12 h-1.5 bg-zinc-300 rounded-full mx-auto -mt-1 mb-2" />

            {/* Cabeçalho do Sheet M3 */}
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-amber-500/15 text-amber-800 flex items-center justify-center border border-amber-500/30">
                  <Plus className="w-4 h-4 stroke-[3]" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900 tracking-tight">Criar Novo Registro</h3>
                  <p className="text-[11px] text-zinc-500">Selecione uma ação rápida no R-Max</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="min-h-12 min-w-12 p-2 rounded-xl text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex items-center justify-center"
                aria-label="Fechar menu de ações"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Grid de Opções M3 */}
            <div className="grid grid-cols-1 gap-2 pt-1">
              {actions.map((act) => {
                const Icon = act.icon;
                return (
                  <button
                    key={act.id}
                    type="button"
                    onClick={() => handleAction(act.id)}
                    className="w-full flex items-center gap-3.5 p-3.5 rounded-2xl border border-zinc-100 hover:border-zinc-300 hover:bg-zinc-50/80 active:scale-[0.99] transition-all cursor-pointer text-left group"
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center border shrink-0 ${act.iconBg} ${act.iconColor} group-hover:scale-105 transition-transform`}>
                      <Icon className="w-5 h-5 stroke-[2.2]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-black text-zinc-900 tracking-tight group-hover:text-amber-800 transition-colors">
                        {act.title}
                      </h4>
                      <p className="text-[11px] text-zinc-500 truncate mt-0.5">
                        {act.subtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
